import React, { useState, useRef, useEffect, useMemo } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

function HolidayPackageForm() {
  const [title, setTitle] = useState("");
  const [destination, setDestination] = useState("");
  const [duration, setDuration] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [description, setDescription] = useState("");
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  const [image, setImage] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [uploadStatus, setUploadStatus] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const fileInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const shortDescRef = useRef(null);
  const fullDescRef = useRef(null);
  const modules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'script': 'sub' }, { 'script': 'super' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      [{ 'direction': 'rtl' }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'font': [] }],
      [{ 'align': [] }],
      ["link", "image", "table"],
      ['clean'],

    ],
    table: true,
    clipboard: { matchVisual: false }
  }), []);

  const formats = useMemo(() => [
    'header', 'font', 'size', 'bold', 'italic', 'underline', 'strike',
    'blockquote', 'code-block', 'list', 'indent', 'script', // Only 'list' is needed
    'direction', 'color', 'background', 'align', 'table', 'link', 'image'
  ], []);
  const handleTableAction = (e, action, targetRef) => {
    e.preventDefault(); // Stop focus from breaking

    // Safely grab the specific editor instance that triggered the button
    const editor = targetRef.current?.getEditor();
    if (!editor) return;

    // Direct access to Quill's active table module for THIS editor
    const tableModule = editor.getModule('table');
    if (!tableModule) {
      toast.error("Table module is not initialized.");
      return;
    }

    // Force the editor to focus so it registers the active cell selection
    editor.focus();

    switch (action) {
      case 'insertRow':
        tableModule.insertRowBelow();
        break;
      case 'insertColumn':
        tableModule.insertColumnRight();
        break;
      case 'deleteRow':
        tableModule.deleteRow();
        break;
      case 'deleteColumn':
        tableModule.deleteColumn();
        break;
      default:
        break;
    }
  };


  // Auto-dismiss success message after 5 seconds
  useEffect(() => {
    if (isSuccess) {
      const timer = setTimeout(() => {
        setIsSuccess(false);
        setUploadStatus("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isSuccess]);

  const handleFileChange = (e) => {
    setImage(e.target.files[0]);
  };

  const handleGalleryChange = (e) => {
    const files = Array.from(e.target.files);
    const mapped = files.map((file) => ({
      file,
      caption: "",
    }));
    setGalleryImages((prev) => [...prev, ...mapped]);
  };

  const updateCaption = (index, value) => {
    setGalleryImages((prev) =>
      prev.map((img, i) =>
        i === index ? { ...img, caption: value } : img
      )
    );
  };

  const removeGalleryImage = (index) => {
    setGalleryImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Reset previous status
    setUploadStatus("");
    setIsSuccess(false);

    if (
      !title ||
      !destination ||
      !duration ||
      !shortDescription ||
      !description ||
      !image
    ) {
      setUploadStatus("❌ Please fill in all fields and upload a main image.");
      return;
    }
    const shortdesc = shortDescription.replace(/&nbsp;/g, " ");
    const fulldesc = description.replace(/&nbsp;/g, " ");
    const formData = new FormData();
    formData.append("title", title);
    formData.append("destination", destination);
    formData.append("duration", duration);
    formData.append("shortDescription", shortdesc);
    formData.append("description", fulldesc);
    formData.append("image", image);

    galleryImages.forEach((img, index) => {
      formData.append("images", img.file);
      formData.append(`captions[${index}]`, img.caption);
    });

    try {
      setIsUploading(true);
      setUploadStatus("⏳ Uploading package...");

      const response = await fetch(
        process.env.REACT_APP_API_URL + "/upload-package",
        {
          method: "POST",
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Upload failed");
      }

      // Success!
      setIsSuccess(true);
      setUploadStatus("✅ Package uploaded successfully!");

      // Reset form after successful upload
      setTitle("");
      setDestination("");
      setDuration("");
      setShortDescription("");
      setDescription("");
      setImage(null);
      setGalleryImages([]);

      if (fileInputRef.current) fileInputRef.current.value = "";
      if (galleryInputRef.current) galleryInputRef.current.value = "";

      // Navigate to packages list after 2 seconds
      setTimeout(() => {
        navigate("/admin/view-packages");
      }, 2000);

    } catch (err) {
      setIsSuccess(false);
      setUploadStatus(`❌ ${err.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="container mb-5">
      <div className="row justify-content-center">
        <div className="col-lg-8">
          <div className="card shadow-lg border-0 rounded-4">
            <div className="card-body p-4 p-md-5">
              <h2 className="text-center mb-4">Add New Package</h2>

              {/* Status Messages */}
              {uploadStatus && (
                <div className={`alert text-center ${isSuccess ? "alert-success" : "alert-danger"
                  } alert-dismissible fade show`} role="alert">
                  <i className={`fa ${isSuccess ? "fa-check-circle" : "fa-exclamation-circle"} me-2`}></i>
                  {uploadStatus}
                  {isSuccess && (
                    <div className="mt-2">
                      <div className="spinner-border spinner-border-sm text-success me-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <small>Redirecting to packages list...</small>
                    </div>
                  )}
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setUploadStatus("");
                      setIsSuccess(false);
                    }}
                  ></button>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {/* TITLE */}
                <div className="form-floating mb-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Package Title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    disabled={isUploading}
                  />
                  <label>Package Title</label>
                </div>

                {/* DESTINATION */}
                <div className="form-floating mb-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Destination"
                    value={destination}
                    onChange={(e) => setDestination(e.target.value)}
                    disabled={isUploading}
                  />
                  <label>Destination</label>
                </div>

                {/* DURATION */}
                <div className="form-floating mb-3">
                  <input

                    type="text"
                    className="form-control"
                    placeholder="Duration"
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    disabled={isUploading}
                  />
                  <label>Duration (e.g. 3 Days)</label>
                </div>
                {/* Create two distinct refs at the top of your component:
    const shortDescRef = useRef(null);
    const fullDescRef = useRef(null); 
*/}

                {/* SHORT DESCRIPTION */}
                <div className="mb-4">
                  <div className="mb-4">
                    <div className="col-12 my-2 clearfix" style={{ minHeight: "300px" }}>
                      <label className="form-label fw-bold">Short Description</label>
                      <div className="quill-wrapper" style={{ height: "300px", marginBottom: "50px" }}>
                        <div className="mb-2"></div>

                        <div className="btn-group btn-group-sm" role="group">
                          {/* Pass shortDescRef specifically to these actions */}
                          <button type="button" className="btn btn-outline-secondary" onMouseDown={(e) => handleTableAction(e, 'insertRow', shortDescRef)}>
                            ➕ Add Row Below
                          </button>
                          <button type="button" className="btn btn-outline-secondary" onMouseDown={(e) => handleTableAction(e, 'insertColumn', shortDescRef)}>
                            ➕ Add Column Right
                          </button>
                          <button type="button" className="btn btn-outline-danger" onMouseDown={(e) => handleTableAction(e, 'deleteRow', shortDescRef)}>
                            ❌ Delete Row
                          </button>
                          <button type="button" className="btn btn-outline-danger" onMouseDown={(e) => handleTableAction(e, 'deleteColumn', shortDescRef)}>
                            ❌ Delete Col
                          </button>
                        </div>

                        <ReactQuill
                          ref={shortDescRef}
                          theme="snow"
                          value={shortDescription}
                          onChange={setShortDescription}
                          readOnly={isUploading}
                          modules={modules}
                          formats={formats}
                          style={{ height: "200px", marginBottom: "40px" }}
                        />
                      </div>
                    </div>
                  </div>
                </div>


                {/* FULL DESCRIPTION */}
                <div className="mb-4">
                  <div className="col-12 my-2 clearfix" style={{ minHeight: "400px" }}>
                    <label className="form-label fw-bold">Full Description</label>
                    <div className="quill-wrapper" style={{ height: "400px", marginBottom: "50px" }}>
                      <div className="mb-2">
                        <div className="btn-group btn-group-sm" role="group">
                          {/* Pass fullDescRef specifically to these actions */}
                          <button type="button" className="btn btn-outline-secondary" onMouseDown={(e) => handleTableAction(e, 'insertRow', fullDescRef)}>
                            ➕ Add Row Below
                          </button>
                          <button type="button" className="btn btn-outline-secondary" onMouseDown={(e) => handleTableAction(e, 'insertColumn', fullDescRef)}>
                            ➕ Add Column Right
                          </button>
                          <button type="button" className="btn btn-outline-danger" onMouseDown={(e) => handleTableAction(e, 'deleteRow', fullDescRef)}>
                            ❌ Delete Row
                          </button>
                          <button type="button" className="btn btn-outline-danger" onMouseDown={(e) => handleTableAction(e, 'deleteColumn', fullDescRef)}>
                            ❌ Delete Col
                          </button>
                        </div>
                        <ReactQuill
                          ref={fullDescRef}
                          theme="snow"
                          value={description}
                          onChange={setDescription}
                          readOnly={isUploading}
                          modules={modules}
                          formats={formats}
                          style={{ height: "250px", marginBottom: "50px" }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* MAIN IMAGE */}
                <div className="mb-4">
                  <label className="form-label fw-semibold">
                    Package Main Image
                  </label>
                  <input
                    type="file"
                    className="form-control"
                    accept="image/*"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    disabled={isUploading}
                  />

                  {image && (
                    <div className="mt-3 text-center">
                      <img
                        src={URL.createObjectURL(image)}
                        alt="Preview"
                        className="img-fluid rounded-3 shadow-sm"
                        style={{ maxHeight: "220px" }}
                      />
                    </div>
                  )}
                </div>

                {/* CAROUSEL IMAGES */}
                <div className="mb-4">
                  <label className="form-label fw-semibold">
                    Carousel Images (Optional)
                  </label>

                  <input
                    type="file"
                    className="form-control"
                    accept="image/*"
                    multiple
                    ref={galleryInputRef}
                    onChange={handleGalleryChange}
                    disabled={isUploading}
                  />

                  {galleryImages.length > 0 && (
                    <div className="row g-3 mt-3">
                      {galleryImages.map((img, index) => (
                        <div className="col-md-4" key={index}>
                          <div className="card p-2 h-100">
                            <img
                              src={URL.createObjectURL(img.file)}
                              className="img-fluid rounded mb-2"
                              style={{ height: 150, objectFit: "cover" }}
                            />

                            <input
                              type="text"
                              className="form-control mb-2"
                              placeholder="Image description (optional)"
                              value={img.caption}
                              onChange={(e) =>
                                updateCaption(index, e.target.value)
                              }
                            />

                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              onClick={() => removeGalleryImage(index)}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* SUBMIT */}
                <div className="d-grid">
                  <button
                    type="submit"
                    className="btn navyblue-bg btn-lg rounded-pill"
                    disabled={isUploading}
                  >
                    {isUploading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Uploading...
                      </>
                    ) : (
                      "Upload Package"
                    )}
                  </button>
                </div>
              </form>

              <div className="text-center mt-3">
                <a href="view-packages" className="text-decoration-none" style={{ color: "#0B214B" }}>
                  <i className="fa fa-arrow-left me-2"></i>
                  Back to Packages
                </a>
              </div>
            </div>
          </div>
        </div>
      </div >
    </div >
  );
}

export default HolidayPackageForm;